import { addPropFrames, placedFrames, type PlacedFramesContext } from "../compile/placed-frames.ts";
import type { CompileContext } from "../compile/context.ts";
import { frameId } from "../assets/manifest.ts";
import type { TileRef, WorldDocument } from "../document/types.ts";
import type { AreaClipboard } from "./area-clipboard.ts";
import type { BrushEntry } from "./brush-set-file.ts";
import { LIBRARY_FRAMES_PER_BATCH } from "./library-held.ts";

/**
 * What a tab asks the library for when it opens a document (ADR-130 decision 2.6): the terrains its layers name,
 * then the frames its view draws, in batches that depend on the document alone. A terrain layer resolves only once
 * its terrain's tables are held, so the first ask names the terrain and the frames that need none, and a later ask,
 * made with the tables, names the rest.
 */

/**
 * What to ask for: terrain ids and frame ids.
 */
export interface OpeningWanted {
  readonly terrains: readonly string[];
  readonly frames: readonly string[];
}

/**
 * Orders two strings by code unit.
 *
 * @param a The first.
 * @param b The second.
 * @returns Negative, zero or positive.
 */
function compareUnits(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * The terrains the terrain layers of every map name.
 *
 * @param document The world document.
 * @returns Each terrain id once, in code-unit order.
 */
export function layerTerrainIds(document: WorldDocument): string[] {
  const ids = new Set<string>();
  for (const map of document.maps) {
    for (const layer of map.layers) {
      if (layer.kind === "terrain") {
        ids.add(layer.terrain);
      }
    }
  }
  return [...ids].sort(compareUnits);
}

/**
 * The terrains a document needs that the context lacks, and the frames it draws that a terrain request does not bring.
 * A terrain layer's tiles are not resolved: asking for its terrain brings the terrain's tilesets whole, so every tile
 * the layer can resolve to comes with it, and resolving a layer to learn its frames costs a second or more on a large
 * map. Everything else `placedFrames` names is asked for: the props' frames, their behaviours' state frames and
 * strips (the strips are in the context once the round before has brought them) and the tile layers' cells. A pack's
 * studio tileset (`<pack>:studio`) that the world lists whole is asked for by its id, since the index offers none of
 * its sprites and the palette lists them as the world's own.
 *
 * @param document The world document.
 * @param context The context now.
 * @returns The terrain ids to ask for and the frame ids the document draws, each in code-unit order.
 * @throws Error when a tile cell names a tileset index past the world's list.
 */
export function openingWanted(document: WorldDocument, context: Pick<CompileContext, "terrains" | "ruleSets" | "transitionStrips">): OpeningWanted {
  const held = new Set(context.terrains.map((terrain) => terrain.id));
  const terrains = layerTerrainIds(document).filter((id) => !held.has(id));
  const unresolved: WorldDocument = { ...document, maps: document.maps.map((map) => ({ ...map, layers: map.layers.filter((layer) => layer.kind !== "terrain") })) };
  const studio = document.tilesets.filter((entry) => entry.frames === undefined && entry.id.endsWith(":studio")).map((entry) => entry.id);
  return { terrains, frames: [...new Set([...placedFrames(unresolved, context), ...studio])].sort(compareUnits) };
}

/**
 * The requests that bring what is wanted and not yet held: the terrain ids first, then the frames, each sorted, cut
 * into batches of at most `LIBRARY_FRAMES_PER_BATCH`. An id already asked for is not asked again, so an id no pack
 * holds is asked once.
 *
 * @param wanted What the document needs.
 * @param isHeld Whether a frame is held.
 * @param asked The ids already asked for.
 * @returns The batches, none when nothing is lacking.
 */
export function openingBatches(wanted: OpeningWanted, isHeld: (frame: string) => boolean, asked: ReadonlySet<string>): string[][] {
  const terrains = [...new Set(wanted.terrains)].filter((id) => !asked.has(id)).sort(compareUnits);
  const frames = [...new Set(wanted.frames)].filter((id) => !asked.has(id) && !isHeld(id)).sort(compareUnits);
  const lacking = [...terrains, ...frames];
  const batches: string[][] = [];
  for (let from = 0; from < lacking.length; from += LIBRARY_FRAMES_PER_BATCH) {
    batches.push(lacking.slice(from, from + LIBRARY_FRAMES_PER_BATCH));
  }
  return batches;
}

/**
 * The terrains the pack index offers on the tilesets a world lists whole, which the context does not yet hold: the
 * terrains the terrain palette offers beyond those its layers paint.
 *
 * @param document The world document.
 * @param terrains The index's terrains, each with its id and its tileset.
 * @param context The context now.
 * @returns The terrain ids, in code-unit order.
 */
export function worldTerrainIds(document: WorldDocument, terrains: readonly { readonly id: string; readonly tileset: string }[], context: Pick<CompileContext, "terrains">): string[] {
  const whole = new Set(document.tilesets.filter((entry) => entry.frames === undefined).map((entry) => entry.id));
  const held = new Set(context.terrains.map((terrain) => terrain.id));
  return terrains.filter((terrain) => whole.has(terrain.tileset) && !held.has(terrain.id)).map((terrain) => terrain.id).sort(compareUnits);
}

/**
 * The tilesets a world lists whole that hold tiles to paint with and no corner terrain: those the context's terrains
 * did not bring and that the pack index offers no prop of. The tile palette lists them with the tilesets the terrains
 * brought. The index says what is a props tileset (it lists a prop of it); what is left is a tile-only tileset.
 *
 * @param document The world document.
 * @param propTilesets The tilesets of the index's props.
 * @param held The tileset ids the catalogue lists now.
 * @returns The tileset ids, in code-unit order.
 */
export function worldTileOnlyIds(document: WorldDocument, propTilesets: ReadonlySet<string>, held: ReadonlySet<string>): string[] {
  return document.tilesets.filter((entry) => entry.frames === undefined && !propTilesets.has(entry.id) && !held.has(entry.id)).map((entry) => entry.id).sort(compareUnits);
}

/**
 * What a builder holds that draws art and is not in the document: the tile in hand, the tile clipboard, the area
 * clipboard, the entries of the prop brush set in hand and frames a panel says it holds (the prop in hand).
 */
export interface HandState {
  readonly tile: TileRef | null;
  readonly clipboard: { readonly cells: readonly (TileRef | null)[] } | null;
  readonly area: AreaClipboard | null;
  readonly brush: readonly BrushEntry[];
  readonly kept: readonly string[];
}

/**
 * The frames of what a builder holds: the tile in hand, the cells of the tile clipboard, the tiles and props (with
 * their behaviours' frames and strips) of the area clipboard, the frames of the prop brush's entries and the frames
 * panels keep. A mirrored tile is the same frame.
 *
 * @param hand What is held.
 * @param strips The context's transition strips, whose frames a behaviour's animation names.
 * @returns The frame ids, each once, in the order met.
 */
export function handFrames(hand: HandState, strips: NonNullable<PlacedFramesContext["transitionStrips"]> = {}): string[] {
  const ids = new Set<string>(hand.kept);
  const add = (ref: TileRef | null): void => {
    if (ref !== null) {
      ids.add(frameId(ref.tileset, null, ref.tile));
    }
  };
  add(hand.tile);
  hand.clipboard?.cells.forEach(add);
  for (const layer of hand.area?.layers ?? []) {
    if (layer.kind === "tile") {
      layer.tiles.forEach(add);
    } else if (layer.kind === "prop") {
      for (const { prop } of layer.props) {
        addPropFrames(prop.frame, prop.behaviour, strips, ids);
      }
    }
  }
  for (const entry of hand.brush) {
    if (entry.kind === "frame") {
      add(entry.frame);
    }
  }
  return [...ids];
}

/**
 * What the document draws or the builder holds that the tab's manifest lacks: the terrains whose layers paint them and
 * whose tiles are not all held (or that the context lacks), and the frames its props, tile layers and hand name. The
 * ids are asked for again whenever the document changes, which covers an undo, a remote operation or a live edit that
 * names art the tab let go of or never held.
 *
 * @param document The world document.
 * @param context The context now.
 * @param held Whether a frame is held.
 * @param hand The frames of the hand.
 * @returns The terrain ids and the frame ids lacking, each in code-unit order.
 * @throws Error when a tile cell names a tileset index past the world's list.
 */
export function missingDrawn(document: WorldDocument, context: Pick<CompileContext, "terrains" | "ruleSets" | "transitionStrips">, held: (frame: string) => boolean, hand: readonly string[] = []): { readonly terrains: string[]; readonly frames: string[] } {
  const wanted = openingWanted(document, context);
  const terrains = new Set(wanted.terrains);
  for (const id of layerTerrainIds(document)) {
    const terrain = context.terrains.find((each) => each.id === id);
    if (terrain !== undefined && terrain.tiles.some((tile) => !held(frameId(terrain.tileset, null, tile.tile)))) {
      terrains.add(id);
    }
  }
  const frames = [...new Set([...wanted.frames, ...hand])].filter((frame) => !held(frame));
  return { terrains: [...terrains].sort(compareUnits), frames: frames.sort(compareUnits) };
}
